import ResponsiveDialogActions from 'app/shared-components/layout/dialog/ResponsiveDialogActions';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import { NO_GROUP } from 'app/pages/cards/cards/NewCardSheet/newCardUtils';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { CardGroup, Role } from 'API';
import type { GetCardsCardGroupPayload } from 'CDK_API';
import { selectCurrentPaidolUser, selectUserCompanies } from 'app/store/userCompaniesSlice';
import { Groups as GroupsIcon } from '@mui/icons-material';
import { getAllGroups, selectAllGroups } from 'app/pages/store/groupsSlice';
import { useAppDispatch, useAppSelector } from 'app/store';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Button,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';

interface AssignGroupModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  onClick: (groupName?: string) => void;
  icon: ReactNode;
  btnText: string;
  currentGroup?: GetCardsCardGroupPayload | null;
}

const AssignGroupModal = ({
  open,
  onClose,
  loading,
  onClick,
  icon,
  btnText,
  currentGroup,
}: AssignGroupModalProps) => {
  const [selectedValue, setSelectedValue] = useState<string>(currentGroup?.id || '');
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { userRole, selectedPaidolId: selectedCompany } = useAppSelector(selectUserCompanies);
  // SEE-1352: scope to the selected company's membership. The old
  // `paidolUsers[0]` both showed a switcher user the wrong company's groups and
  // threw outright when the array was empty.
  const currentPaidolUser = useAppSelector(selectCurrentPaidolUser);

  const managedGroups = useMemo(
    () => currentPaidolUser?.managedGroups?.items.map((item) => item?.cardGroup),
    [currentPaidolUser]
  );
  const groups = useAppSelector(selectAllGroups);

  const availableGroups = useMemo(() => {
    const groupsToUse = userRole === Role.GROUP_ADMINISTRATOR ? managedGroups : groups;
    return (
      groupsToUse
        ?.filter((group): group is CardGroup => !!group)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })) || []
    );
  }, [userRole, managedGroups, groups]);

  useEffect(() => {
    dispatch(getAllGroups(selectedCompany));
  }, [dispatch, selectedCompany, open]);

  useEffect(() => {
    setSelectedValue(currentGroup?.id || '');
  }, [currentGroup]);

  const handleChange = (event: { target: { value: any } }) => {
    const value = event.target.value;
    setSelectedValue(value);
  };

  const onSave = () => {
    onClick(selectedValue);
    setSelectedValue('');
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle
        id="alert-dialog-title"
        color="primary"
        sx={{
          fontSize: '2.4rem',
          mt: '2rem',
          p: '2rem 5rem 1rem',
          alignSelf: 'center',
        }}
      >
        {t('cardActionModal.moveToAnotherGroup.title')}
      </DialogTitle>
      <DialogContent sx={{ p: '0 5rem' }}>
        <Typography sx={{ pt: '1rem', pb: '3rem' }}>
          {t('cardActionModal.moveToAnotherGroup.message')}
        </Typography>

        <FormControl fullWidth>
          <InputLabel id="cardGroupId-label">{t('assignToGroup')}</InputLabel>
          <Select
            id="cardGroupId"
            labelId="cardGroupId-label"
            label={t('assignToGroup')}
            value={selectedValue}
            onChange={handleChange}
            startAdornment={
              <InputAdornment position="start">
                <GroupsIcon />
              </InputAdornment>
            }
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
          >
            <MenuItem value={NO_GROUP}>{t('noGroup')}</MenuItem>

            {availableGroups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <ResponsiveDialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('cancel')}
        </Button>
        <LoadingButton
          loading={loading}
          variant="contained"
          onClick={onSave}
          startIcon={icon}
          loadingPosition="start"
          color="primary"
        >
          {btnText}
        </LoadingButton>
      </ResponsiveDialogActions>
    </ResponsiveDialog>
  );
};

export default AssignGroupModal;
